import { nrError } from '@/util/newrelic-browser';

export function constructTYPO3Url(
  uid: string,
  forceAddLangParam: string = '',
  staticRoute: boolean = false,
  hash: string = ''
): string {
  // Construct the url with all params
  const url: URL = new URL(location.search, location.href);
  const langParam: string = new URLSearchParams(location.search).get('L') ?? '';

  // Delete cHash and glossary params, always
  url.searchParams.delete('cHash');
  url.searchParams.delete('tx_systimeglossary_pi1[character]');

  // Edge case for handling static routes
  if (staticRoute) {
    // Delete params from search
    url.searchParams.delete('id');
    url.searchParams.delete('page');
  }

  if (uid !== '' && !staticRoute) {
    // Overwrite current uid
    url.searchParams.set('id', uid);
  } else {
    url.searchParams.delete('id');
  }
  if (langParam !== '') {
    // Add L if missing
    url.searchParams.set('L', langParam);
  }
  if (forceAddLangParam !== '') {
    // Add L forced
    url.searchParams.set('L', forceAddLangParam);
  }

  if (langParam === '0' || forceAddLangParam === '0') {
    url.searchParams.delete('L');
  }

  if (url.search.includes('page')) {
    url.searchParams.delete('page');
  }

  // Delete search q in url
  if (url.search.includes('q')) {
    url.searchParams.delete('q');
  }

  // Delete filter query from url
  if (url.search.includes('filter')) {
    url.searchParams.delete('filter');
  }

  // Return constructed url if static route
  if (staticRoute) {
    return `${uid}${url.search}`;
  }

  // Return relative url with hash
  if (hash) {
    return `/${url.search}#c${hash}`;
  }

  // Return relative url with all params and overwritten
  return `/${url.search}`;
}

export function featureGalleryUrlWithParams(
  featureGalleryUrl: string,
  lang: number = 0
): string {
  const langParam: string = new URLSearchParams(location.search).get('L') ?? '';
  const featureGalleryUrlNormalized: URL = new URL(featureGalleryUrl);

  // Add L-var from url if present - overrules lang="xx" if exists
  if (langParam && langParam !== '-1') {
    featureGalleryUrlNormalized.searchParams.set('L', langParam.toString());
  } else {
    // Use lang from html-tag
    featureGalleryUrlNormalized.searchParams.set('L', lang.toString());
  }

  return featureGalleryUrlNormalized.href;
}

export function externalTypo3Url(url: string): string {
  const feedbackUrl: URL = new URL(url);
  const langParam: string = new URLSearchParams(location.search).get('L') ?? '';

  if (langParam !== '') {
    // Add L if missing
    feedbackUrl.searchParams.set('L', langParam);
  }
  if (langParam === '0') {
    feedbackUrl.searchParams.delete('L');
  }

  return feedbackUrl.href;
}

export function getSignInUrl(accountUrl: string) {
  try {
    // iBook url
    const url: URL = new URL(location.search, location.href);
    const langParam: string =
      new URLSearchParams(location.search).get('L') ?? '';
    // Account url
    // eslint-disable-next-line camelcase
    const accountUrl_URL = new URL(accountUrl);
    const redirectParam =
      new URLSearchParams(accountUrl_URL.search).get('redirect_url') ?? '';

    // Append url hash if set #c123
    if (window.location.hash) {
      url.hash = window.location.hash;
    }
    // If current iBook page has L-var, add to myaccount url as well
    if (langParam !== '') {
      accountUrl_URL.searchParams.set('L', langParam);
    }

    // Update redirect url
    if (redirectParam !== '') {
      accountUrl_URL.searchParams.set('redirect_url', url.href);
    }

    return accountUrl_URL.href;
  } catch (e: any) {
    nrError(e.message, {
      action: 'Could not create signin url',
      component: 'typo3Urls.ts'
    });
    // eslint-disable-next-line no-console
    console.warn(e, 'Could not create signin url');

    return '';
  }
}

export function getSignOutUrl(accountUrl: string): string {
  try {
    // iBook url
    const url: URL = new URL(location.search, location.href);
    // Account url
    // eslint-disable-next-line camelcase
    const accountUrl_URL = new URL(accountUrl);
    // Add redirect back to iBog
    accountUrl_URL.searchParams.set('redirect_url', url.href);

    return accountUrl_URL.href;
  } catch (e: any) {
    nrError(e.message, {
      action: 'Could not create signout url',
      component: 'typo3Urls.ts'
    });
    // eslint-disable-next-line no-console
    console.warn(e, 'Could not create signout url');

    return '';
  }
}
